import { useEffect, useMemo, useRef, useState } from "react";

import type { ComboboxOption } from "./Combobox";

/** Debounce window before an async `loadOptions` query fires. */
export const COMBOBOX_DEBOUNCE_MS = 200;

export type ComboboxFilterFn = (
  option: ComboboxOption,
  query: string,
) => boolean;

export const defaultComboboxFilter: ComboboxFilterFn = (option, query) =>
  option.label.toLowerCase().includes(query.toLowerCase());

export type UseComboboxFilterParams = {
  options: ComboboxOption[];
  query: string;
  filter: ComboboxFilterFn;
  /** When present, the hook switches from sync filtering to debounced fetch. */
  loadOptions?: (query: string) => Promise<ComboboxOption[]>;
  /** Caller-driven loading flag, OR-ed with the hook's own pending state. */
  loading?: boolean;
  /** Gates the async fetch — when false, no `loadOptions` call fires (e.g. before
   *  the field is focused/open). Only affects the async path. @default true */
  enabled?: boolean;
};

export type UseComboboxFilterResult = {
  /** Empty while `loading` — the UI swaps rows for a loader, so consumers
   *  reading item counts / committing selections stay correct by construction. */
  visibleOptions: ComboboxOption[];
  loading: boolean;
  /** True when the latest async load rejected; cleared by the next successful load. */
  error: boolean;
};

/**
 * Owns the two mutually exclusive option-resolution strategies:
 *
 * - **sync** (no `loadOptions`): `options` filtered by `filter` on every render,
 *   zero timers — the component stays purely synchronous.
 * - **async** (`loadOptions` present): debounce the query, fetch, and commit only
 *   the *latest* call's result. Each effect run owns a closure-scoped `cancelled`
 *   flag (StrictMode-safe — survives mount→cleanup→remount) and a monotonic
 *   request id guards cross-request staleness. `loadOptions` is read through a
 *   latest-ref so an inline prop doesn't re-arm the debounce every parent render.
 */
export const useComboboxFilter = ({
  options,
  query,
  filter,
  loadOptions,
  loading,
  enabled = true,
}: UseComboboxFilterParams): UseComboboxFilterResult => {
  const syncOptions = useMemo(
    () => options.filter(option => filter(option, query)),
    [options, query, filter],
  );

  const [asyncOptions, setAsyncOptions] = useState<ComboboxOption[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const latestRequestId = useRef(0);
  const loadOptionsRef = useRef(loadOptions);
  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  });

  const isAsync = loadOptions !== undefined;

  useEffect(() => {
    if (!isAsync || !enabled) {
      return;
    }
    // Show the loader immediately on a query change, not after the debounce, so the
    // list never shows the previous query's stale results during the wait.
    setPending(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      const load = loadOptionsRef.current;
      if (load === undefined) {
        setPending(false);
        return;
      }
      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;
      load(query).then(
        result => {
          if (cancelled || requestId !== latestRequestId.current) {
            return;
          }
          setAsyncOptions(result);
          setError(false);
          setPending(false);
        },
        () => {
          if (cancelled || requestId !== latestRequestId.current) {
            return;
          }
          setAsyncOptions([]);
          setError(true);
          setPending(false);
        },
      );
    }, COMBOBOX_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAsync, enabled, query]);

  if (!isAsync) {
    const isLoading = Boolean(loading);
    return {
      visibleOptions: isLoading ? [] : syncOptions,
      loading: isLoading,
      error: false,
    };
  }

  const isLoading = pending || Boolean(loading);
  return {
    visibleOptions: isLoading ? [] : asyncOptions,
    loading: isLoading,
    error,
  };
};
